export type EmailFlowTrigger =
  | 'payment_paid'
  | 'payment_pending'
  | 'checkout_abandoned'
  | 'payment_failed';

export type EmailFlowRecipient = 'customer' | 'admin';

export interface EmailTemplate {
  id?: string;
  templateKey: string;
  name: string;
  alias: string;
  subject: string;
  htmlBody: string;
  textBody: string;
  messageStream: string;
  fromEmail?: string;
  replyTo?: string;
  active: boolean;
  postmarkTemplateId?: number;
  sampleModel: Record<string, unknown>;
  lastSyncedAt?: string;
}

export interface EmailFlow {
  id?: string;
  flowKey: string;
  name: string;
  description: string;
  triggerType: EmailFlowTrigger;
  recipientType: EmailFlowRecipient;
  templateAlias: string;
  messageStream: string;
  delayMinutes: number;
  maxSends: number;
  enabled: boolean;
}

export interface EmailHistoryEntry {
  id: string;
  source: string;
  status: string;
  to: string;
  templateAlias: string;
  messageStream: string;
  tag: string;
  subject: string;
  messageId: string;
  submittedAt: string;
  error: string;
}

type EmailAutomationStore = {
  templates: EmailTemplate[];
  flows: EmailFlow[];
};

const EMAIL_AUTOMATION_URL = '/api/admin/email-automation';

const parseServerResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Erreur email automation');
  }
  return payload as T;
};

const loadServerStore = async (): Promise<EmailAutomationStore> => {
  const response = await fetch(EMAIL_AUTOMATION_URL, {
    credentials: 'include',
    cache: 'no-store',
  });
  const payload = await parseServerResponse<Partial<EmailAutomationStore>>(response);
  return {
    templates: Array.isArray(payload.templates) ? payload.templates : [],
    flows: Array.isArray(payload.flows) ? payload.flows : [],
  };
};

const saveServerItem = async (
  kind: 'template' | 'flow',
  item: EmailTemplate | EmailFlow
): Promise<void> => {
  const response = await fetch(EMAIL_AUTOMATION_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(kind === 'template' ? { kind, template: item } : { kind, flow: item }),
  });
  await parseServerResponse<{ ok: true }>(response);
};

export async function ensureDefaults(): Promise<void> {
  await loadServerStore();
}

export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  return (await loadServerStore()).templates;
}

export async function saveEmailTemplate(template: EmailTemplate): Promise<void> {
  await saveServerItem('template', template);
}

export async function getEmailFlows(): Promise<EmailFlow[]> {
  return (await loadServerStore()).flows;
}

export async function saveEmailFlow(flow: EmailFlow): Promise<void> {
  await saveServerItem('flow', flow);
}

export async function getEmailHistory(limit = 100): Promise<EmailHistoryEntry[]> {
  const response = await fetch(`/api/admin/email-history?limit=${encodeURIComponent(String(limit))}`, {
    credentials: 'include',
    cache: 'no-store',
  });
  const payload = await parseServerResponse<{ history?: EmailHistoryEntry[] }>(response);
  return Array.isArray(payload.history) ? payload.history : [];
}

export const emailAutomationService = {
  ensureDefaults,
  getEmailTemplates,
  saveEmailTemplate,
  getEmailFlows,
  saveEmailFlow,
  getEmailHistory,
};
